import React, { useEffect } from 'react';
import { useState } from 'react';
import { getSftpFiles } from '../../api/client';

import { Box, Button, CircularProgress, Card, Divider, IconButton, Typography, Skeleton, Stack } from '@mui/material';
import { ArrowBack, FolderOutlined, InsertDriveFileOutlined, CancelPresentation } from '@mui/icons-material';

import { getSelectedFiles, updateSelectedFiles } from '../../api/client';
import { Alert } from '../popupAlerts';
import { useSelector } from 'react-redux';

import * as UIAction from '../../redux/actions/ui.actions';
import * as Selectors from '../../redux/selectors';

import FileUpload from '../../assets/file-upload.svg';

const SFTPFileList = () => {
  const tenant = useSelector((state) => state.configuration.tenant_id);
  const flow = useSelector((state) => state.flows.selectedFlow);

  const [tree, setTree] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadingSelectedFiles, setLoadingSelectedFiles] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const getFiles = async () => {
      try {
        setLoading(true);
        const res = await getSftpFiles();
        setTree(res);
      } finally {
        setLoading(false);
      }
    };
    getFiles();
  }, []);

  useEffect(() => {
    const getFiles = async () => {
      setLoadingSelectedFiles(true);
      const res = await getSelectedFiles();
      setLoadingSelectedFiles(false);
      setSelected(res.files);
    };
    getFiles();
  }, []);

  const handleClick = async (file) => {
    if (file.is_dir) {
      setLoading(true);
      const res = await getSftpFiles(file.path);
      setHistory([...history, file.path]);
      setLoading(false);
      setTree(res.subDir);
    }

    if (!file.is_dir) {
      if (!selected.find((path) => path === file.path)) {
        setSelected([...selected, file.path]);
      } else {
        setSelected(selected.filter((path) => path !== file.path));
      }
    }
  };

  const handleBack = async () => {
    setHistory([...history.slice(0, -1)]);
    setLoading(true);
    const res = await getSftpFiles(history[history.length - 2]);
    setLoading(false);
    setTree(res.subDir || res);
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      await updateSelectedFiles(selected);
      // Call the onSFTPSave listener here, if it is set
      const listener = Selectors.getListener();
      if (listener && listener.onSFTPSave) {
        listener.onSFTPSave(selected, tenant, flow);
      }
      Alert({ text: 'Files saved!', type: 'success' });
      UIAction.goBack();
    } catch (e) {
      Alert({ text: 'Something went wrong!', type: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Box mb={'8px'}>
        <Typography variant="body1">Select which files to import</Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        flex="1"
        width="100%"
        overflow="hidden"
        gap={2}
      >
        <Card
          display="flex"
          flexDirection="column"
          alignItems="start"
          elevation={0}
          sx={{
            width: '100%',
            overflowY: 'auto',
            p: 2,
            pt: 0,
            border: ({ palette }) => `1px solid ${palette.common.outline}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            pb={2}
            style={{ cursor: 'pointer' }}
            position="sticky"
            pt={2}
            backgroundColor="common.white"
            top="0"
          >
            {history.length > 0 ? (
              <>
                <IconButton
                  style={{
                    width: 24,
                    height: 24,
                    border: `1px solid #ccc`,
                    marginRight: 16,
                  }}
                  onClick={handleBack}
                >
                  <ArrowBack style={{ fontSize: '14px' }} />
                </IconButton>
                <Typography variant="body2">Back to {history[history.length - 2] || 'root'}</Typography>
              </>
            ) : (
              <Typography variant="body2">/root</Typography>
            )}
          </Box>
          {loading && (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={258} height={24} />
              <Skeleton variant="rectangular" width={258} height={24} />
              <Skeleton variant="rectangular" width={258} height={24} />
            </Stack>
          )}
          {!loading &&
            tree.map((file) => (
              <React.Fragment key={file.path}>
                <Box
                  display="flex"
                  sx={{
                    cursor: 'pointer',
                    height: 32,
                    width: '100%',
                    py: 1,
                    marginTop: history.length === 0 ? '0px' : 'initial',
                    ':hover': {
                      backgroundColor: '#eee',
                    },
                  }}
                  alignItems="center"
                  onClick={() => handleClick(file)}
                >
                  {file.is_dir ? <FolderOutlined fontSize={'16px'} /> : <InsertDriveFileOutlined fontSize={'16px'} />}
                  <Typography style={{ marginLeft: 4 }} variant="body1" fontWeight="300">
                    {file.name}
                  </Typography>
                </Box>
                <Divider />
              </React.Fragment>
            ))}
        </Card>
        <Card
          display="flex"
          flexDirection="column"
          alignItems="start"
          elevation={0}
          sx={{
            p: 2,
            width: '80%',
            overflowY: 'auto',
            border: ({ palette }) => `1px solid ${palette.common.outline}`,
            flexDirection: 'column',
            alignItems: 'start',
            display: 'flex',
          }}
        >
          <Typography style={{ marginBottom: '16px' }} variant="body2">
            Selected files
          </Typography>
          {loadingSelectedFiles ? (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={200} height={24} />
              <Skeleton variant="rectangular" width={200} height={24} />
            </Stack>
          ) : selected.length > 0 ? (
            selected.map((path) => (
              <Box
                key={path}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => setSelected(selected.filter((file) => file !== path))}
                sx={{
                  cursor: 'pointer',
                  height: 32,
                  p: 0.5,
                  mb: 0.5,
                  gap: 1,
                  width: '100%',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  border: ({ palette }) => `1px solid ${palette.common.outline}`,
                  marginTop: history.length === 0 ? '0px' : 'initial',
                  ':hover': {
                    backgroundColor: '#eee',
                  },
                }}
              >
                <Typography style={{ marginLeft: 4, maxWidth: 160 }} variant="body2">
                  {path}
                </Typography>
                <CancelPresentation style={{ fontSize: '16px' }} />
              </Box>
            ))
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              height="80%"
              paddingTop="24px"
              gap={3}
            >
              <Box component="img" src={FileUpload} alt="File upload icon" width="40px" height="40px" />
              <Box>
                <Typography sx={{ mb: 1 }} variant="body1">
                  Select from File Explorer
                </Typography>
                <Typography variant="body2" fontWeight="300" color="text.lightish">
                  You can select one or more files to import.
                </Typography>
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      <Box
        display="flex"
        justifyContent={'space-between'}
        py={2}
        position="sticky"
        bottom={0}
        bgcolor="background.paper"
        zIndex={10}
      >
        <Button variant="outlined" color="text" onClick={() => UIAction.goBack()}>
          Cancel
        </Button>
        <Button
          disabled={selected.length == 0 || saveLoading}
          onClick={handleSave}
          variant="contained"
          color="accent"
          sx={{ fontSize: '14px', px: 4 }}
          form="app-params-form"
          type="submit"
        >
          {saveLoading && <CircularProgress style={{ width: '16px', height: '16px' }} />}
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default SFTPFileList;
